import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Loader2, Copy as CopyIcon, Instagram, Music2, Youtube, HelpCircle, Package } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function partnerAuthHeader() {
    try {
        const t = localStorage.getItem("eigoo_partner_token");
        return t ? { Authorization: `Bearer ${t}` } : {};
    } catch { return {}; }
}

export default function PartnerAIStudio() {
    const [tab, setTab] = useState("product");
    const tabs = [
        { id: "product", label: "Product Copy", icon: Package },
        { id: "social", label: "Social Captions", icon: Instagram },
        { id: "msa", label: "Agreement Q&A", icon: HelpCircle },
    ];
    return (
        <div className="rounded-sm border border-forest/15 bg-cream-warm overflow-hidden" data-testid="partner-ai-studio">
            <div className="px-5 py-4 border-b border-forest/15 flex items-center gap-2 bg-cream">
                <Sparkles size={16} className="text-gold" />
                <h2 className="font-serif text-xl text-forest">AI Studio</h2>
                <span className="ml-2 text-[0.6rem] tracking-[0.3em] uppercase text-brown/60">Ymaku's Autopilot</span>
            </div>
            <div className="flex flex-wrap border-b border-forest/15 bg-cream">
                {tabs.map((t) => {
                    const I = t.icon;
                    return (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            className={`px-4 py-3 text-[0.7rem] tracking-[0.22em] uppercase inline-flex items-center gap-2 border-b-2 ${
                                tab === t.id ? "border-gold text-forest" : "border-transparent text-brown/70 hover:text-forest"
                            }`}
                            data-testid={`partner-ai-tab-${t.id}`}
                        >
                            <I size={13} /> {t.label}
                        </button>
                    );
                })}
            </div>
            <div className="p-5">
                {tab === "product" && <ProductCopyPane />}
                {tab === "social" && <SocialCaptionPane />}
                {tab === "msa" && <MsaQaPane />}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
function copyToClip(text) {
    try { navigator.clipboard.writeText(text); toast.success("Copied"); } catch { /* ignore */ }
}

function ProductCopyPane() {
    const [name, setName] = useState("");
    const [notes, setNotes] = useState("");
    const [vibe, setVibe] = useState("");
    const [out, setOut] = useState(null);
    const [busy, setBusy] = useState(false);
    const run = async () => {
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/ai/product-copy`, {
                product_name: name, ingredient_notes: notes, vibe: vibe || undefined,
            }, { headers: partnerAuthHeader() });
            setOut(data);
            toast.success("Copy generated");
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };
    return (
        <div className="grid lg:grid-cols-2 gap-5" data-testid="partner-ai-product-pane">
            <div className="space-y-3">
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Product name (e.g. Island Twilight Bar)" className="w-full px-3 py-2.5 rounded-sm bg-cream border border-forest/20 text-sm" data-testid="partner-ai-product-name" />
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} placeholder="Ingredient notes — your shorthand (e.g. hibiscus, kukui, pink clay, evening)" className="w-full px-3 py-2.5 rounded-sm bg-cream border border-forest/20 text-sm" data-testid="partner-ai-product-notes" />
                <input value={vibe} onChange={(e) => setVibe(e.target.value)} placeholder="Vibe (optional): grounding evening ritual" className="w-full px-3 py-2.5 rounded-sm bg-cream border border-forest/20 text-sm" data-testid="partner-ai-product-vibe" />
                <button onClick={run} disabled={busy || !name || !notes} className="btn-pill btn-gold disabled:opacity-60" data-testid="partner-ai-product-run">
                    {busy ? <><Loader2 size={14} className="animate-spin" /> Writing…</> : <><Sparkles size={14} /> Draft Product Copy</>}
                </button>
            </div>
            <div className="rounded-sm border border-forest/15 p-5 bg-cream text-sm" data-testid="partner-ai-product-out">
                {out ? (
                    <div className="space-y-3 text-forest/90">
                        <div><strong className="text-forest">Headline:</strong> {out.headline} <button onClick={() => copyToClip(out.headline)} className="text-brown/50 hover:text-gold ml-1"><CopyIcon size={11} /></button></div>
                        <div><strong>Tagline:</strong> {out.tagline}</div>
                        <div><strong>Description:</strong> <p className="mt-1 whitespace-pre-wrap">{out.long_description}</p></div>
                        <div><strong>Bullets:</strong> <ul className="list-disc pl-5 mt-1 space-y-0.5">{(out.bullets || []).map((b, i) => <li key={i}>{b}</li>)}</ul></div>
                        <div><strong>Scent:</strong> {out.scent_profile}</div>
                        <div><strong>Ritual:</strong> {out.ritual_use}</div>
                    </div>
                ) : <p className="text-brown/50">Output will appear here.</p>}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
function SocialCaptionPane() {
    const [context, setContext] = useState("");
    const [out, setOut] = useState(null);
    const [busy, setBusy] = useState(false);
    const run = async () => {
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/ai/social-caption`, {
                context, platforms: ["instagram", "tiktok", "youtube"],
            }, { headers: partnerAuthHeader() });
            setOut(data);
            toast.success("Captions ready");
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };
    return (
        <div className="space-y-4" data-testid="partner-ai-social-pane">
            <textarea
                value={context} onChange={(e) => setContext(e.target.value)}
                rows={3}
                placeholder="Describe the post (e.g. ASMR cutting video of Island Gold Glow turmeric bar, golden close-up)"
                className="w-full px-3 py-2.5 rounded-sm bg-cream border border-forest/20 text-sm"
                data-testid="partner-ai-social-context"
            />
            <button onClick={run} disabled={busy || !context} className="btn-pill btn-gold disabled:opacity-60" data-testid="partner-ai-social-run">
                {busy ? <><Loader2 size={14} className="animate-spin" /> Writing…</> : <><Sparkles size={14} /> Generate Captions</>}
            </button>
            {out && (
                <div className="grid md:grid-cols-3 gap-4" data-testid="partner-ai-social-out">
                    {out.instagram && (
                        <div className="rounded-sm border border-forest/15 p-4 bg-cream">
                            <div className="flex items-center gap-2 text-[0.65rem] tracking-[0.3em] uppercase text-gold mb-2"><Instagram size={12} /> Instagram</div>
                            <p className="text-sm whitespace-pre-wrap text-forest/90">{out.instagram.caption}</p>
                            <p className="mt-2 text-xs text-brown/70">{(out.instagram.hashtags || []).join(" ")}</p>
                        </div>
                    )}
                    {out.tiktok && (
                        <div className="rounded-sm border border-forest/15 p-4 bg-cream">
                            <div className="flex items-center gap-2 text-[0.65rem] tracking-[0.3em] uppercase text-gold mb-2"><Music2 size={12} /> TikTok</div>
                            <p className="text-sm whitespace-pre-wrap text-forest/90">{out.tiktok.caption}</p>
                            <p className="mt-2 text-xs text-brown/70">{(out.tiktok.hashtags || []).join(" ")}</p>
                        </div>
                    )}
                    {out.youtube && (
                        <div className="rounded-sm border border-forest/15 p-4 bg-cream">
                            <div className="flex items-center gap-2 text-[0.65rem] tracking-[0.3em] uppercase text-gold mb-2"><Youtube size={12} /> YouTube</div>
                            <p className="text-sm font-semibold text-forest">{out.youtube.title}</p>
                            <p className="text-sm mt-1 whitespace-pre-wrap text-forest/90">{out.youtube.description}</p>
                            <p className="mt-2 text-xs text-brown/70">{(out.youtube.tags || []).join(", ")}</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
function MsaQaPane() {
    const [question, setQuestion] = useState("");
    const [messages, setMessages] = useState([]);
    const [session, setSession] = useState(null);
    const [busy, setBusy] = useState(false);
    const ask = async () => {
        if (!question.trim()) return;
        const q = question;
        setQuestion("");
        setMessages((m) => [...m, { role: "partner", text: q }]);
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/ai/msa-qa`, {
                question: q, session_id: session || undefined,
            }, { headers: partnerAuthHeader() });
            setSession(data.session_id);
            setMessages((m) => [...m, { role: "assistant", text: data.answer }]);
        } catch (e) {
            setMessages((m) => [...m, { role: "assistant", text: `Error: ${e?.response?.data?.detail || "Failed"}` }]);
        } finally { setBusy(false); }
    };
    return (
        <div className="space-y-3" data-testid="partner-ai-msa-pane">
            <div className="rounded-sm border border-forest/15 p-4 bg-cream max-h-96 overflow-y-auto space-y-3">
                {messages.length === 0 && (
                    <p className="text-sm text-brown/50">
                        Ask the Agreement anything. Your signed Creator Agreement is the only source — answers are grounded on it.
                    </p>
                )}
                {messages.map((m, i) => (
                    <div key={i} className={`text-sm ${m.role === "partner" ? "text-forest" : "text-brown"}`}>
                        <span className="text-[0.6rem] tracking-[0.3em] uppercase text-brown/50 block">{m.role === "partner" ? "You" : "Agreement"}</span>
                        <p className="whitespace-pre-wrap">{m.text}</p>
                    </div>
                ))}
                {busy && <div className="text-xs text-brown/60 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Reading the agreement…</div>}
            </div>
            <div className="flex gap-2">
                <input
                    value={question} onChange={(e) => setQuestion(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !busy && ask()}
                    placeholder="e.g. What's my revenue share percentage?"
                    className="flex-1 px-3 py-2.5 rounded-sm bg-cream border border-forest/20 text-sm"
                    data-testid="partner-ai-msa-input"
                />
                <button onClick={ask} disabled={busy || !question.trim()} className="btn-pill btn-gold disabled:opacity-60" data-testid="partner-ai-msa-send">Ask</button>
            </div>
        </div>
    );
}
